/**
 * Draft preparation board state. One plain JSON object, so saving/sharing later is "store this, read it back through parseDraft".
 * Several named drafts live in a DraftBook, stored in this browser only.
 */

// Riot role naming (same as the scout report), plus the ban list as a sixth slot so picks and bans share every code path
export const SLOTS = ["TOP", "JUNGLE", "MIDDLE", "BOTTOM", "UTILITY", "BANS"] as const;
export type Slot = (typeof SLOTS)[number];
export const SLOT_LABEL: Record<Slot, string> = { TOP: "Top", JUNGLE: "Jungle", MIDDLE: "Mid", BOTTOM: "Bot", UTILITY: "Support", BANS: "Bans" };

export const SIDES = ["ours", "theirs"] as const;
export type Side = (typeof SIDES)[number];

/** Champion ids (numeric Riot key) per slot, highest priority first. */
export type DraftSide = Record<Slot, number[]>;

export interface DraftPrep {
	v: 1;
	ours: DraftSide;
	theirs: DraftSide;
}

// pre draft-sets key, only read for migration (left in place so a rollback still finds it)
const DRAFT_KEY = "riftwatch.draft";
const MAX_PER_SLOT = 20;

const emptySide = (): DraftSide => ({ TOP: [], JUNGLE: [], MIDDLE: [], BOTTOM: [], UTILITY: [], BANS: [] });

export function emptyDraft(): DraftPrep {
	return { v: 1, ours: emptySide(), theirs: emptySide() };
}

const withSlot = (d: DraftPrep, side: Side, slot: Slot, ids: number[]): DraftPrep => ({ ...d, [side]: { ...d[side], [slot]: ids } });

/** Adds the champion at the end of the slot (lowest priority), or removes it when it is already there. */
export function toggleChampion(d: DraftPrep, side: Side, slot: Slot, championId: number): DraftPrep {
	const ids = d[side][slot];
	if (ids.includes(championId)) return withSlot(d, side, slot, ids.filter(id => id !== championId));
	return ids.length < MAX_PER_SLOT ? withSlot(d, side, slot, [...ids, championId]) : d;
}

/** Moves a champion one step up (-1) or down (+1) in priority. No-op at the edges. */
export function moveChampion(d: DraftPrep, side: Side, slot: Slot, index: number, delta: -1 | 1): DraftPrep {
	const ids = [...d[side][slot]];
	const to = index + delta;
	if (index < 0 || index >= ids.length || to < 0 || to >= ids.length) return d;
	[ids[index], ids[to]] = [ids[to]!, ids[index]!];
	return withSlot(d, side, slot, ids);
}

/** The only way stored (later: shared) data becomes a draft: anything malformed is dropped, never thrown on. */
export function parseDraft(raw: unknown): DraftPrep {
	const d = emptyDraft();
	if (!raw || typeof raw !== "object") return d;
	for (const side of SIDES) {
		const src = (raw as Record<string, unknown>)[side];
		if (!src || typeof src !== "object") continue;
		for (const slot of SLOTS) {
			const ids = (src as Record<string, unknown>)[slot];
			if (Array.isArray(ids)) d[side][slot] = [...new Set(ids.filter((n): n is number => Number.isInteger(n) && n > 0))].slice(0, MAX_PER_SLOT);
		}
	}
	return d;
}

/** A named draft. Several of them make a book, so different opponents / plans can be prepared side by side. */
export interface DraftSet {
	id: string;
	name: string;
	draft: DraftPrep;
}

export interface DraftBook {
	v: 1;
	activeId: string;
	sets: DraftSet[];
}

const BOOK_KEY = "riftwatch.drafts";
const MAX_SETS = 20;
const MAX_NAME = 40;

const newSet = (name: string, draft = emptyDraft()): DraftSet => ({ id: crypto.randomUUID(), name, draft });

/** First "Draft N" not taken yet, so new sets never collide by name. */
function freeName(b: DraftBook): string {
	const taken = new Set(b.sets.map(s => s.name));
	let n = 1;
	while (taken.has(`Draft ${n}`)) n++;
	return `Draft ${n}`;
}

export function emptyBook(draft = emptyDraft()): DraftBook {
	const set = newSet("Draft 1", draft);
	return { v: 1, activeId: set.id, sets: [set] };
}

export const activeSet = (b: DraftBook): DraftSet => b.sets.find(s => s.id === b.activeId) ?? b.sets[0]!;

export function updateActive(b: DraftBook, draft: DraftPrep): DraftBook {
	const id = activeSet(b).id;
	return { ...b, sets: b.sets.map(s => (s.id === id ? { ...s, draft } : s)) };
}

export function selectSet(b: DraftBook, id: string): DraftBook {
	return b.sets.some(s => s.id === id) ? { ...b, activeId: id } : b;
}

/** Appends an empty set and switches to it. */
export function addSet(b: DraftBook): DraftBook {
	if (b.sets.length >= MAX_SETS) return b;
	const set = newSet(freeName(b));
	return { ...b, activeId: set.id, sets: [...b.sets, set] };
}

/** Copies a set right after the original and switches to the copy. */
export function duplicateSet(b: DraftBook, id: string): DraftBook {
	const i = b.sets.findIndex(s => s.id === id);
	if (i < 0 || b.sets.length >= MAX_SETS) return b;
	const src = b.sets[i]!;
	const copy = newSet(`${src.name} (copy)`.slice(0, MAX_NAME), src.draft);
	return { ...b, activeId: copy.id, sets: [...b.sets.slice(0, i + 1), copy, ...b.sets.slice(i + 1)] };
}

/** Blank names are ignored, so a set always keeps a visible tab label. */
export function renameSet(b: DraftBook, id: string, name: string): DraftBook {
	const clean = name.trim().slice(0, MAX_NAME);
	if (!clean) return b;
	return { ...b, sets: b.sets.map(s => (s.id === id ? { ...s, name: clean } : s)) };
}

/** Never deletes the last set (that one can only be cleared). Deleting the active set moves to its neighbour. */
export function deleteSet(b: DraftBook, id: string): DraftBook {
	const i = b.sets.findIndex(s => s.id === id);
	if (i < 0 || b.sets.length <= 1) return b;
	const sets = b.sets.filter(s => s.id !== id);
	return { ...b, sets, activeId: b.activeId === id ? sets[Math.min(i, sets.length - 1)]!.id : b.activeId };
}

/** Same contract as parseDraft: malformed sets are dropped, the result always has at least one set. */
export function parseBook(raw: unknown): DraftBook {
	const src = raw && typeof raw === "object" ? (raw as Record<string, unknown>).sets : null;
	const sets: DraftSet[] = [];
	const ids = new Set<string>();
	for (const item of Array.isArray(src) ? src : []) {
		if (!item || typeof item !== "object" || sets.length >= MAX_SETS) continue;
		const { id, name, draft } = item as Record<string, unknown>;
		const cleanId = typeof id === "string" && id && !ids.has(id) ? id : crypto.randomUUID();
		const cleanName = typeof name === "string" ? name.trim().slice(0, MAX_NAME) : "";
		ids.add(cleanId);
		sets.push({ id: cleanId, name: cleanName || `Draft ${sets.length + 1}`, draft: parseDraft(draft) });
	}
	if (!sets.length) return emptyBook();
	const activeId = (raw as Record<string, unknown>).activeId;
	return { v: 1, sets, activeId: typeof activeId === "string" && ids.has(activeId) ? activeId : sets[0]!.id };
}

const readJson = (key: string): unknown => {
	try {
		return JSON.parse(localStorage.getItem(key) ?? "null");
	} catch {
		return null;
	}
};

/** Falls back to the old single-draft key, so a board made before draft sets becomes "Draft 1". */
export function loadBook(): DraftBook {
	const raw = readJson(BOOK_KEY);
	return raw ? parseBook(raw) : emptyBook(parseDraft(readJson(DRAFT_KEY)));
}

export function saveBook(b: DraftBook): void {
	try {
		localStorage.setItem(BOOK_KEY, JSON.stringify(b));
	} catch {
		/* not persisted */
	}
}
